import { useTranslation } from 'react-i18next';
import { cn } from '@shared/lib';
import { Modal } from '@shared/ui';
import type { LearnerLevel } from '../api/storiesApi';

export const LEARNER_LEVELS: LearnerLevel[] = ['zero', 'basic', 'confident'];

/** Три варианта уровня кнопками: в окне вопроса и на стартовом экране демо. */
export function LevelChoice({
  value,
  onPick,
  busy,
  compact,
}: {
  value: LearnerLevel | null;
  onPick: (level: LearnerLevel) => void;
  busy?: boolean;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div role="radiogroup" aria-label={t('stories.level.title')} className={cn('flex gap-2', compact ? 'flex-wrap' : 'flex-col')}>
      {LEARNER_LEVELS.map((level) => (
        <button
          key={level}
          type="button"
          role="radio"
          aria-checked={value === level}
          disabled={busy}
          onClick={() => onPick(level)}
          className={cn(
            'lx-px-sm text-left transition-colors disabled:opacity-60',
            compact ? 'px-3 py-1.5 text-[14px] font-medium' : 'px-4 py-3',
            value === level ? 'bg-[#f2c14e] text-[#1a120e]' : 'bg-[#1d2040] text-[#e6e7f5] hover:bg-[#2a2d55]',
          )}
        >
          <span className={cn('block font-semibold', !compact && 'text-[16px]')}>{t(`stories.level.${level}.title`)}</span>
          {!compact && (
            <span className={cn('mt-0.5 block text-[14px] leading-snug', value === level ? 'text-[#3a2a10]' : 'text-[#a9acd0]')}>
              {t(`stories.level.${level}.text`)}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

/**
 * «Как у тебя с английским?» — один раз при первом входе в игру (владелец 2026-10-25, «для тех, кто учит с нуля»): от
 * ответа зависят простота речи ведущего, перевод по умолчанию и вставка подсказок («С нуля»). Поменять — на карте игры.
 */
export function LevelPrompt({
  open,
  value,
  busy,
  onPick,
  onClose,
}: {
  open: boolean;
  value: LearnerLevel | null;
  busy?: boolean;
  onPick: (level: LearnerLevel) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Modal open={open} onClose={onClose} placement="sheet" width={480} kicker={t('stories.level.kicker')} title={t('stories.level.title')}>
      <p className="text-[15px] leading-relaxed text-[#d6d6e2]">{t('stories.level.sub')}</p>
      <LevelChoice value={value} onPick={onPick} busy={busy} />
      <p className="text-[13px] text-muted">{t('stories.level.change')}</p>
    </Modal>
  );
}
